import React, { useState } from 'react';
import {
  Title,
  Text,
  Label,
  Input,
  CheckBox,
  Button,
  MessageStrip,
  Icon,
  TabContainer,
  Tab,
} from '@ui5/webcomponents-react';
import useAutoTranslate from '../../../hooks/useAutoTranslate';
import { CONFIG_STYLES } from '../configStyles';

function FarosBiConfig({ formData, setFormData, systemSettings, onSave, isLoading }) {
  const [activeSubTab, setActiveSubTab] = useState('reports');
  const { autoT } = useAutoTranslate();

  const farosBiSettings = systemSettings?.FAROSBI;

  if (!formData) {
    return <div>{autoT('Cargando configuración FarosBi...')}</div>;
  }

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const hasChanges = () => {
    const saved = farosBiSettings?.reports || {};
    return (
      formData.reportId        !== (saved.reportId        || '') ||
      formData.tenantId        !== (saved.tenantId        || '') ||
      formData.token           !== (saved.token           || '') ||
      formData.companyId       !== (saved.companyId       || '') ||
      formData.multipleReports !== (saved.multipleReports ?? false) ||
      formData.baseUrl         !== (saved.baseUrl         || '') ||
      formData.filtro          !== (saved.filtro          || '') ||
      formData.criterio        !== (saved.criterio        || '')
    );
  };

  const prepareDataForSave = () => ({
    ...(farosBiSettings || {}),
    reports: {
      reportId:        formData.reportId,
      tenantId:        formData.tenantId,
      token:           formData.token,
      companyId:       formData.companyId,
      multipleReports: formData.multipleReports,
      baseUrl:         formData.baseUrl,
      filtro:          formData.filtro,
      criterio:        formData.criterio,
    },
    ingestData: farosBiSettings?.ingestData || {},
  });

  const handleSave = () => onSave(prepareDataForSave());

  return (
    <div style={CONFIG_STYLES.page}>
      {/* Header */}
      <div style={CONFIG_STYLES.headerRow}>
        <div style={CONFIG_STYLES.headerTitle}>
          <Title level="H3">FAROSBI</Title>
          <Text style={CONFIG_STYLES.labelHint}>
            {autoT('Integración con la plataforma FarosBi — Suite FLN')}
          </Text>
        </div>
        {activeSubTab === 'reports' && (
          <Button
            design="Emphasized"
            onClick={handleSave}
            disabled={!hasChanges() || isLoading}
            icon="save"
          >
            {isLoading ? autoT('Guardando...') : autoT('Guardar Cambios')}
          </Button>
        )}
      </div>

      {/* Sub-tabs */}
      <TabContainer
        onTabSelect={(e) => {
          const id = e.detail.tab.getAttribute('data-subtab-id');
          if (id) setActiveSubTab(id);
        }}
        fixed={true}
        style={{ marginBottom: '1.5rem' }}
      >
        <Tab
          text={autoT('Reportes')}
          icon="chart-bar-and-line"
          data-subtab-id="reports"
          selected={activeSubTab === 'reports'}
        />
        <Tab
          text={autoT('Ingesta de Datos')}
          icon="download-from-cloud"
          data-subtab-id="ingestData"
          selected={activeSubTab === 'ingestData'}
        />
      </TabContainer>

      {/* Sub-tab: Reportes */}
      {activeSubTab === 'reports' && (
        <>
          {hasChanges() && (
            <MessageStrip design="Information" style={{ marginBottom: '1rem' }}>
              {autoT('Hay cambios pendientes de guardar')}
            </MessageStrip>
          )}

          <div style={CONFIG_STYLES.sectionCard}>
            <div style={{
              ...CONFIG_STYLES.sectionDivider,
              borderLeft: '3px solid var(--sapHighlightColor)',
              paddingLeft: '0.75rem',
            }}>
              <Title level="H4">{autoT('Configuración de Reportes')}</Title>
            </div>

            <div style={CONFIG_STYLES.formGrid}>
              <div style={CONFIG_STYLES.fieldGroup}>
                <Label required>Report ID</Label>
                <Input
                  style={{ width: '100%' }}
                  placeholder="10103"
                  value={formData.reportId}
                  onInput={(e) => handleChange('reportId', e.target.value)}
                />
                <Text style={CONFIG_STYLES.labelHint}>
                  {autoT('ID del reporte principal en FarosBi')}
                </Text>
              </div>

              <div style={CONFIG_STYLES.fieldGroup}>
                <Label required>Tenant ID</Label>
                <Input
                  style={{ width: '100%' }}
                  placeholder="1"
                  value={formData.tenantId}
                  onInput={(e) => handleChange('tenantId', e.target.value)}
                />
                <Text style={CONFIG_STYLES.labelHint}>
                  {autoT('Identificador del tenant en FarosBi')}
                </Text>
              </div>

              <div style={CONFIG_STYLES.fieldGroup}>
                <Label required>Token</Label>
                <Input
                  type="Password"
                  style={{ width: '100%' }}
                  placeholder="••••••••"
                  value={formData.token}
                  onInput={(e) => handleChange('token', e.target.value)}
                />
                <Text style={CONFIG_STYLES.labelHint}>
                  {autoT('Token de autenticación de la API FarosBi')}
                </Text>
              </div>

              <div style={CONFIG_STYLES.fieldGroup}>
                <Label required>Company ID</Label>
                <Input
                  style={{ width: '100%' }}
                  placeholder="1"
                  value={formData.companyId}
                  onInput={(e) => handleChange('companyId', e.target.value)}
                />
                <Text style={CONFIG_STYLES.labelHint}>
                  {autoT('ID de la empresa en FarosBi')}
                </Text>
              </div>

              <div style={CONFIG_STYLES.fieldGroup}>
                <Label required>Base URL</Label>
                <Input
                  style={{ width: '100%' }}
                  placeholder="https://api.farosbi.com:8443"
                  value={formData.baseUrl}
                  onInput={(e) => handleChange('baseUrl', e.target.value)}
                />
                <Text style={CONFIG_STYLES.labelHint}>
                  {autoT('URL base de la API FarosBi (sin barra final)')}
                </Text>
              </div>

              <div style={CONFIG_STYLES.fieldGroup}>
                <Label>Filtro</Label>
                <Input
                  style={{ width: '100%' }}
                  placeholder="$filter=..."
                  value={formData.filtro}
                  onInput={(e) => handleChange('filtro', e.target.value)}
                />
                <Text style={CONFIG_STYLES.labelHint}>
                  {autoT('Filtro global que se concatena al embedUrl de todos los reportes (opcional)')}
                </Text>
              </div>

              <div style={CONFIG_STYLES.fieldGroup}>
                <Label>Criterio</Label>
                <Input
                  style={{ width: '100%' }}
                  placeholder="$criteria=..."
                  value={formData.criterio}
                  onInput={(e) => handleChange('criterio', e.target.value)}
                />
                <Text style={CONFIG_STYLES.labelHint}>
                  {autoT('Criterio global que se concatena al embedUrl de todos los reportes (opcional)')}
                </Text>
              </div>
            </div>

            <div style={{ ...CONFIG_STYLES.switchRow, marginTop: '1.5rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <Label>{autoT('Activar Reportes múltiples?')}</Label>
                <Text style={CONFIG_STYLES.labelHint}>
                  {autoT('Si está activo, se cargan todos los reportes del tenant en el Home')}
                </Text>
              </div>
              <CheckBox
                checked={formData.multipleReports}
                onChange={(e) => handleChange('multipleReports', e.target.checked)}
              />
            </div>
          </div>

          <div style={CONFIG_STYLES.infoBox}>
            <div style={CONFIG_STYLES.infoHeader}>
              <Icon name="information" style={{ color: 'var(--sapHighlightColor)' }} />
              <Title level="H5">{autoT('Información')}</Title>
            </div>
            <ul style={{ color: 'var(--sapContent_LabelColor)', margin: 0, paddingLeft: '1.25rem', lineHeight: '1.8' }}>
              <li>
                <Text>{autoT('Con "Reportes múltiples" desactivado se muestra el reporte indicado por Report ID en el Home')}</Text>
              </li>
              <li>
                <Text>{autoT('Con "Reportes múltiples" activado se carga la lista de reportes del tenant y se muestra un selector en el Home')}</Text>
              </li>
              <li>
                <Text>{autoT('El token debe corresponder a un usuario FarosBi con acceso al tenant configurado')}</Text>
              </li>
            </ul>
          </div>
        </>
      )}

      {/* Sub-tab: Ingesta de Datos (placeholder v1) */}
      {activeSubTab === 'ingestData' && (
        <div style={CONFIG_STYLES.sectionCard}>
          <div style={CONFIG_STYLES.sectionDivider}>
            <Title level="H4">{autoT('Ingesta de Datos')}</Title>
          </div>
          <div style={CONFIG_STYLES.infoBox}>
            <div style={CONFIG_STYLES.infoHeader}>
              <Icon name="in-progress" style={{ color: 'var(--sapHighlightColor)' }} />
              <Title level="H5">{autoT('Próximamente')}</Title>
            </div>
            <Text style={{ color: 'var(--sapContent_LabelColor)' }}>
              {autoT('La configuración de ingesta de datos estará disponible en una próxima versión.')}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}

export default FarosBiConfig;
